import { yupResolver } from "@hookform/resolvers/yup";
import { Reorder } from "@mui/icons-material";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import CCService from "../../services/httpServiceWithoutSuperAdmin";
import ImageUploader from "../Events/ImageUploader";

/* ---------------------------- validation schema --------------------------- */
const schema = yup.object().shape({
  event_id: yup.string().required("Event Type required"),
  venue_id: yup.string().required("Venue required"),
  name: yup.string().required("Session name required"),
  start_time: yup.date().required("Start time required"),
  end_time: yup.date().required("End time required"),
  tickets: yup
    .array()
    .of(
      yup.object().shape({
        sku_id: yup.string().required("SKU required"),
        ticket_name: yup.string().required("Ticket name required"),
        capacity: yup
          .number()
          .typeError("Capacity must be a number")
          .positive("Capacity must be positive")
          .required("Capacity required"),
      })
    )
    .min(1, "At least one ticket type is required"),
  tags: yup.array().of(yup.string()).nullable(),
  metadata: yup
    .array()
    .of(
      yup.object().shape({
        key: yup.string().required("Heading required"),
        value: yup.string().required("Detail required"),
      })
    )
    .nullable(),
  organizers: yup
    .object()
    .shape({
      title: yup.string().nullable(),
      organizer_ids: yup.array().of(yup.string()).nullable(),
      extra: yup.string().nullable(),
    })
    .nullable(),
  // SEO meta_data (optional; tighten with required() if needed)
  meta_data: yup
    .object()
    .shape({
      title: yup.string().nullable(),
      description: yup.string().nullable(),
      keywords: yup.string().nullable(), // UI as comma-separated
      og_image: yup.string().url("Must be a valid URL").nullable(),
      og_title: yup.string().nullable(),
      og_description: yup.string().nullable(),
    })
    .nullable(),
});

/* ----------------------------- helper utils ------------------------------- */
const toDateTimeLocal = (epochMs) =>
  epochMs ? dayjs(Number(epochMs)).format("YYYY-MM-DDTHH:mm") : "";

/* ---------------------- Organizers Autocomplete component ------------------ */
function OrganizersAutocomplete({
  organizersProp = [],
  value = [],
  onChange,
  placeholder = "Type to search organizers...",
  maxSuggestions = 20,
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);

  const normalizedAll = Array.isArray(organizersProp) ? organizersProp : [];

  const suggestions = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return normalizedAll.slice(0, maxSuggestions);
    return normalizedAll
      .filter((o) => {
        const hay = `${o.name || ""} ${o.company || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, maxSuggestions);
  }, [normalizedAll, query, maxSuggestions]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const addOrganizer = (org) => {
    const id = String(org.id);
    if (!Array.isArray(value)) onChange([id]);
    else if (!value.includes(id)) onChange([...value, id]);
    setQuery("");
    setOpen(false);
    setActiveIndex(-1);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (open && activeIndex >= 0 && suggestions[activeIndex]) {
        e.preventDefault();
        addOrganizer(suggestions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls="organizer-listbox"
        aria-activedescendant={
          activeIndex >= 0 ? `org-${activeIndex}` : undefined
        }
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActiveIndex(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        style={{
          width: "100%",
          padding: "10px 12px",
          borderRadius: 8,
          border: "1px solid #E5E7EB",
          outline: "none",
          boxSizing: "border-box",
        }}
      />

      {open && suggestions.length > 0 && (
        <ul
          id="organizer-listbox"
          role="listbox"
          aria-label="Organizers suggestions"
          style={{
            position: "absolute",
            zIndex: 60,
            top: "calc(100% + 8px)",
            left: 0,
            right: 0,
            background: "white",
            border: "1px solid #E5E7EB",
            borderRadius: 8,
            maxHeight: 260,
            overflow: "auto",
            boxShadow: "0 6px 20px rgba(2,6,23,0.08)",
          }}
        >
          {suggestions.map((s, idx) => {
            const isActive = idx === activeIndex;
            const isSelected =
              Array.isArray(value) && value.includes(String(s.id));
            return (
              <li
                key={s.id}
                id={`org-${idx}`}
                role="option"
                aria-selected={isSelected}
                onMouseDown={(e) => {
                  e.preventDefault();
                  addOrganizer(s);
                }}
                onMouseEnter={() => setActiveIndex(idx)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "8px 10px",
                  cursor: isSelected ? "not-allowed" : "pointer",
                  background: isActive ? "#F8FAFF" : "white",
                  opacity: isSelected ? 0.6 : 1,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    {s.company}
                  </div>
                </div>
                {isSelected ? (
                  <div style={{ fontSize: 12, color: "#6b7280" }}>Selected</div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <div style={{ marginTop: 8, color: "#6b7280", fontSize: 13 }}>
        Type to search — press <kbd>Enter</kbd> to add, <kbd>Esc</kbd> to close.
      </div>
    </div>
  );
}

/* ------------------------------- SessionForm ----------------------------- */
export default function SessionForm({
  eventType = [],
  venues = [],
  sku = [],
  onSuccess,
  organizers: organizersProp = [],
  initialData = null,
}) {
  const services = new CCService();

  const preparedDefaultValues = useMemo(() => {
    if (!initialData) return undefined;

    const out = { ...initialData };

    // Normalize metadata to [{ key, value }]
    if (Array.isArray(initialData.metadata)) {
      out.metadata = initialData.metadata
        .slice()
        .sort((a, b) => {
          const ao = typeof a.order === "number" ? a.order : 0;
          const bo = typeof b.order === "number" ? b.order : 0;
          return ao - bo;
        })
        .map((m) => {
          const title = m.title ?? m.key ?? "";
          const rawValue = m.value ?? "";
          const value =
            rawValue !== null && typeof rawValue === "object"
              ? JSON.stringify(rawValue)
              : String(rawValue);
          return { key: title, value };
        });
    }

    // Normalize organizers
    if (!out.organizers) out.organizers = {};
    else if (
      Array.isArray(initialData.organizers) &&
      initialData.organizers.length
    ) {
      out.organizers.organizer_ids = initialData.organizers.map((o) => o.id);
    }
    if (
      !out.organizers.organizer_ids &&
      Array.isArray(initialData.organizer_ids)
    ) {
      out.organizers.organizer_ids = initialData.organizer_ids;
    }

    // Normalize SEO meta_data for form
    const md = initialData.meta_data || {};
    out.meta_data = {
      title: md.title ?? "",
      description: md.description ?? "",
      keywords: Array.isArray(md.keywords)
        ? md.keywords.join(", ")
        : md.keywords ?? "",
      og_image: md.og_image ?? "",
      og_title: md.og_title ?? "",
      og_description: md.og_description ?? "",
    };

    return out;
  }, [initialData]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: preparedDefaultValues,
    resolver: yupResolver(schema),
  });

  const {
    fields: ticketFields,
    append: appendTicket,
    remove: removeTicket,
  } = useFieldArray({ control, name: "tickets" });

  const {
    fields: metadataFields,
    append: appendMeta,
    remove: removeMeta,
    move: moveMeta,
  } = useFieldArray({ control, name: "metadata" });

  // Organizer roles
  const selectedOrganizerIds = watch("organizers.organizer_ids") || [];
  const [organizerRoles, setOrganizerRoles] = useState({}); // id -> role

  useEffect(() => {
    const ids = Array.isArray(selectedOrganizerIds)
      ? selectedOrganizerIds.map(String)
      : [];
    setOrganizerRoles((prev) => {
      const out = { ...prev };

      let initialRoles = {};
      if (initialData) {
        if (
          initialData.organizer_roles &&
          typeof initialData.organizer_roles === "object"
        ) {
          initialRoles = { ...initialData.organizer_roles };
        } else if (Array.isArray(initialData.organizers)) {
          initialData.organizers.forEach((o) => {
            if (o?.id && o?.role) initialRoles[String(o.id)] = o.role;
          });
        }
      }

      for (const id of ids) {
        if (initialRoles[String(id)])
          out[String(id)] = initialRoles[String(id)];
        else if (!out[String(id)]) out[String(id)] = "Host";
      }
      Object.keys(out).forEach((k) => {
        if (!ids.includes(k)) delete out[k];
      });

      return out;
    });
  }, [selectedOrganizerIds, initialData]);

  const changeOrganizerRole = (id, role) => {
    setOrganizerRoles((p) => ({ ...p, [String(id)]: role }));
  };

  /* ------------------------------ styles --------------------------------- */
  const styles = {
    form: {
      display: "block",
      width: 700,
      maxWidth: "98vw",
      padding: 16,
      borderRadius: 10,
      background: "#ffffff",
      boxSizing: "border-box",
    },
    heading: { fontSize: 18, fontWeight: 600, marginBottom: 16 },
    fieldWrap: { marginBottom: 12 },
    label: { display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 },
    input: {
      width: "100%",
      padding: "10px 12px",
      fontSize: 14,
      borderRadius: 8,
      border: "1px solid #E5E7EB",
      boxSizing: "border-box",
      outline: "none",
    },
    textarea: {
      width: "100%",
      padding: "10px 12px",
      fontSize: 14,
      borderRadius: 8,
      border: "1px solid #E5E7EB",
      minHeight: 80,
      resize: "vertical",
      boxSizing: "border-box",
      outline: "none",
    },
    grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
    card: {
      padding: 12,
      borderRadius: 8,
      background: "#F9FAFB",
      border: "1px solid rgba(0,0,0,0.05)",
      marginTop: 8,
    },
    addBtn: {
      marginTop: 12,
      background: "#2563eb",
      color: "#fff",
      border: "none",
      borderRadius: 6,
      padding: "8px 14px",
      fontSize: 13,
      fontWeight: 500,
      cursor: "pointer",
    },
    removeBtn: {
      background: "transparent",
      border: "none",
      color: "#DC2626",
      cursor: "pointer",
      fontSize: 13,
    },
    errorText: { color: "#DC2626", marginTop: 6, fontSize: 13 },
    organizerPreview: {
      display: "flex",
      gap: 12,
      flexWrap: "wrap",
      marginTop: 8,
    },
    organizerTile: {
      display: "flex",
      gap: 8,
      alignItems: "center",
      padding: "8px 10px",
      borderRadius: 8,
      background: "#fff",
      border: "1px solid #EDEFF2",
      minWidth: 240,
    },
    dragHandle: {
      width: 28,
      height: 28,
      borderRadius: 6,
      border: "1px dashed rgba(0,0,0,0.12)",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: 8,
      cursor: "grab",
      background: "#fff",
    },
  };

  /* ------------------------ drag and drop handlers ------------------------ */
  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (source.index === destination.index) return;
    moveMeta(source.index, destination.index);
  };

  const onSubmit = async (data) => {
    try {
      const venue = venues.find((v) => Number(v.id) === Number(data.venue_id));
      const snapshot = {
        name: venue?.name || null,
        address_line1: venue?.address_line1 || null,
      };

      // Build metadata (drag-list) into array with order
      const metadata = (data.metadata || [])
        .map(({ key, value }, index) => {
          if (!key) return null;
          let parsedValue;
          try {
            parsedValue = JSON.parse(value);
          } catch {
            parsedValue = value;
          }
          return { title: key, value: parsedValue, order: index };
        })
        .filter(Boolean);

      const selectedIds = Array.isArray(data.organizers?.organizer_ids)
        ? data.organizers.organizer_ids
        : [];

      const organizer_ids = selectedIds.map((id) => {
        const parsedId =
          typeof id === "string" && /^\d+$/.test(id) ? Number(id) : id;
        return { id: parsedId, role: organizerRoles[String(id)] || "Host" };
      });

      // Build SEO meta_data object (keywords as array)
      const mdForm = data.meta_data || {};
      const keywordsArray =
        typeof mdForm.keywords === "string" && mdForm.keywords.trim()
          ? mdForm.keywords
              .split(",")
              .map((k) => k.trim())
              .filter(Boolean)
          : Array.isArray(mdForm.keywords)
          ? mdForm.keywords
          : [];

      const meta_data = {
        title: mdForm.title || "",
        description: mdForm.description || "",
        keywords: keywordsArray,
        og_image: mdForm.og_image || "",
        og_title: mdForm.og_title || "",
        og_description: mdForm.og_description || "",
      };

      const payload = {
        description: data.description || "",
        banner: data.banner || "",
        name: data.name || "",
        tickets: data.tickets || [],
        event_id: Number(data.event_id),
        venue_id: Number(data.venue_id),
        start_time: dayjs(data.start_time).valueOf(),
        end_time: dayjs(data.end_time).valueOf(),
        tags: Array.isArray(data.tags)
          ? data.tags.filter((t) => t && t.trim() !== "")
          : [],
        metadata, // drag/drop custom fields (array)
        venue_snapshot: snapshot,
        organizer_ids, // [{ id, role }]
        seo_meta_data: meta_data, // SEO block (object)
      };

      if (initialData?.id) {
        await services.put(`/ahead/updateSession/${initialData.id}`, payload);
      } else {
        await services.post(`/ahead/addSession`, payload);
      }

      onSuccess?.();
    } catch (err) {
      console.error(err);
      alert("Failed to save session");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={styles.form}>
      <h2 style={styles.heading}>Session Details</h2>

      {/* Event Type */}
      <div style={styles.fieldWrap}>
        <label style={styles.label}>Event Type</label>
        <select {...register("event_id")} style={styles.input}>
          <option value="">Select Event</option>
          {eventType.map((et) => (
            <option key={et.id} value={et.id}>
              {et.title}
            </option>
          ))}
        </select>
        {errors.event_id && (
          <p style={styles.errorText}>{errors.event_id.message}</p>
        )}
      </div>

      {/* Session Name */}
      <div style={styles.fieldWrap}>
        <label style={styles.label}>Session Name</label>
        <input {...register("name")} style={styles.input} />
        {errors.name && <p style={styles.errorText}>{errors.name.message}</p>}
      </div>

      {/* Venue */}
      <div style={styles.fieldWrap}>
        <label style={styles.label}>Venue</label>
        <select {...register("venue_id")} style={styles.input}>
          <option value="">Select Venue</option>
          {venues.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}, {v.city}
            </option>
          ))}
        </select>
        {errors.venue_id && (
          <p style={styles.errorText}>{errors.venue_id.message}</p>
        )}
      </div>

      {/* Description */}
      <div style={styles.fieldWrap}>
        <label style={styles.label}>Description</label>
        <textarea {...register("description")} style={styles.textarea} />
      </div>

      {/* SEO Meta Data */}

      {/* Start / End Time */}
      <div style={styles.grid2}>
        <div>
          <label style={styles.label}>Start Time</label>
          <input
            type="datetime-local"
            {...register("start_time")}
            style={styles.input}
          />
          {errors.start_time && (
            <p style={styles.errorText}>{errors.start_time.message}</p>
          )}
        </div>
        <div>
          <label style={styles.label}>End Time</label>
          <input
            type="datetime-local"
            {...register("end_time")}
            style={styles.input}
          />
          {errors.end_time && (
            <p style={styles.errorText}>{errors.end_time.message}</p>
          )}
        </div>
      </div>

      {/* Banner */}
      <div style={styles.fieldWrap}>
        <label style={styles.label}>Banner</label>
        <ImageUploader
          label="Banner"
          name="banner"
          value={watch("banner")}
          onUrlChange={(name, url) => setValue("banner", url)}
        />
      </div>

      {/* Tags */}
      <div style={styles.fieldWrap}>
        <label style={styles.label}>Tags (comma separated)</label>
        <input
          type="text"
          value={(watch("tags") || []).join(", ")}
          onChange={(e) => {
            const value = e.target.value.split(",").map((t) => t.trim());
            setValue("tags", value);
          }}
          style={styles.input}
          placeholder="e.g. tech, keynote, ai"
        />
      </div>

      {/* Tickets */}
      <div style={styles.fieldWrap}>
        <h3 style={{ fontWeight: 600, marginBottom: 8 }}>Tickets</h3>
        {ticketFields.map((field, index) => (
          <div key={field.id} style={styles.card}>
            <div style={styles.grid2}>
              <div>
                <label style={styles.label}>SKU</label>
                <select
                  {...register(`tickets.${index}.sku_id`)}
                  style={styles.input}
                >
                  <option value="">Select SKU</option>
                  {sku.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={styles.label}>Ticket Name</label>
                <input
                  {...register(`tickets.${index}.ticket_name`)}
                  style={styles.input}
                />
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <label style={styles.label}>Capacity</label>
              <input
                type="number"
                {...register(`tickets.${index}.capacity`)}
                style={styles.input}
              />
            </div>
            <button
              type="button"
              onClick={() => removeTicket(index)}
              style={styles.removeBtn}
            >
              Remove Ticket
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            appendTicket({ sku_id: "", ticket_name: "", capacity: 0 })
          }
          style={styles.addBtn}
        >
          + Add Ticket
        </button>
      </div>

      {/* Metadata (with drag-and-drop reorder) */}
      <div style={styles.fieldWrap}>
        <h3 style={{ fontWeight: 600, marginBottom: 8 }}>Metadata</h3>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="metadata-droppable">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {metadataFields.map((field, index) => (
                  <Draggable
                    key={field.id}
                    draggableId={String(field.id)}
                    index={index}
                  >
                    {(draggableProvided, snapshot) => (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        style={{
                          ...styles.card,
                          ...draggableProvided.draggableProps.style,
                          boxShadow: snapshot.isDragging
                            ? "0 4px 12px rgba(0,0,0,0.08)"
                            : undefined,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <div
                            {...draggableProvided.dragHandleProps}
                            style={styles.dragHandle}
                            aria-hidden
                            title="Drag to reorder"
                          >
                            <Reorder />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={styles.grid2}>
                              <div>
                                <label style={styles.label}>
                                  Heading (key)
                                </label>
                                <input
                                  {...register(`metadata.${index}.key`)}
                                  style={styles.input}
                                  placeholder="e.g. stage"
                                />
                              </div>
                              <div>
                                <label style={styles.label}>
                                  Detail (value)
                                </label>
                                <input
                                  {...register(`metadata.${index}.value`)}
                                  style={styles.input}
                                  placeholder="e.g. Main Hall"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div style={{ marginTop: 8 }}>
                          <button
                            type="button"
                            onClick={() => removeMeta(index)}
                            style={styles.removeBtn}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <button
          type="button"
          onClick={() => appendMeta({ key: "", value: "" })}
          style={styles.addBtn}
        >
          + Add Metadata Field
        </button>
      </div>

      {/* Organizers */}
      <div style={styles.fieldWrap}>
        <h3 style={{ fontWeight: 600, marginBottom: 8 }}>Organizers</h3>

        <div style={{ marginTop: 8 }}>
          <OrganizersAutocomplete
            organizersProp={organizersProp}
            value={watch("organizers.organizer_ids") || []}
            onChange={(ids) => setValue("organizers.organizer_ids", ids)}
            placeholder="Search and select organizers..."
          />
        </div>

        <div style={styles.organizerPreview}>
          {(watch("organizers.organizer_ids") || []).map((id) => {
            const obj = organizersProp.find((o) => String(o.id) === String(id));
            if (!obj) return null;
            return (
              <div key={obj.id} style={styles.organizerTile}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{obj.name}</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    {obj.company}
                  </div>
                </div>

                <div style={{ minWidth: 160 }}>
                  <label
                    style={{ fontSize: 12, display: "block", color: "#374151" }}
                  >
                    Role
                  </label>
                  <input
                    type="text"
                    value={organizerRoles[String(obj.id)] || "Host"}
                    onChange={(e) =>
                      changeOrganizerRole(obj.id, e.target.value)
                    }
                    style={{
                      borderRadius: 6,
                      padding: "6px 8px",
                      border: "1px solid #E5E7EB",
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                    placeholder="e.g. Host, Keynote Speaker, Panel Moderator"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const current = Array.isArray(
                      watch("organizers.organizer_ids")
                    )
                      ? watch("organizers.organizer_ids")
                      : [];
                    setValue(
                      "organizers.organizer_ids",
                      current.filter((x) => String(x) !== String(obj.id))
                    );
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#ef4444",
                    cursor: "pointer",
                  }}
                >
                  Remove
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ ...styles.fieldWrap, marginTop: 16 }}>
        <h3 style={{ fontWeight: 600, marginBottom: 8 }}>SEO Meta Data</h3>

        <div style={styles.grid2}>
          <div>
            <label style={styles.label}>Title</label>
            <input
              {...register("meta_data.title")}
              style={styles.input}
              placeholder="SEO title"
            />
          </div>
          <div>
            <label style={styles.label}>OG Title</label>
            <input
              {...register("meta_data.og_title")}
              style={styles.input}
              placeholder="Open Graph title"
            />
          </div>
        </div>

        <div style={{ marginTop: 8 }}>
          <label style={styles.label}>Description</label>
          <textarea
            {...register("meta_data.description")}
            style={styles.textarea}
            placeholder="Meta description"
          />
        </div>

        <div style={{ marginTop: 8 }}>
          <label style={styles.label}>OG Description</label>
          <textarea
            {...register("meta_data.og_description")}
            style={styles.textarea}
            placeholder="Open Graph description"
          />
        </div>

        <div style={styles.grid2}>
          <div>
            <label style={styles.label}>OG Image URL</label>
            {/* <input
              {...register("meta_data.og_image")}
              style={styles.input}
              placeholder="https://example.com/og-image.jpg"
            /> */}

            <ImageUploader
              label="OG Image"
              name="meta_data.og_image"
              value={watch("meta_data.og_image")}
              onUrlChange={(name, url) => setValue("meta_data.og_image", url)}
              requiredWidth={1280}
              requiredHeight={630}
            />
          </div>
          <div>
            <label style={styles.label}>Keywords (comma separated)</label>
            <input
              {...register("meta_data.keywords")}
              style={styles.input}
              placeholder="event, tech, keynote"
            />
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        style={{
          ...styles.addBtn,
          backgroundColor: "#4F46E5",
          width: "100%",
          marginTop: 20,
        }}
      >
        {isSubmitting ? "Saving..." : "Save Session"}
      </button>
    </form>
  );
}
